/**
 * Deterministic random number generation.
 *
 * ARCHITECTURAL RULE: nothing in the game may call Math.random(). Every random
 * decision comes from a *named* stream created here, and every stream's state is
 * part of the saved game. That gives us three things we cannot get any other way:
 *
 *   1. A bug report is a seed plus a save — we can reproduce exactly what happened.
 *   2. Golden tests can assert that a seed always produces an identical world.
 *   3. Adding a new random call to one system cannot shift the sequence another
 *      system sees, because each system draws from its own stream.
 *
 * The generator is sfc32 (small fast counting): 128 bits of state in four uint32s,
 * excellent statistical quality, and — critically — it is trivially serialisable,
 * unlike anything built on BigInt or closures.
 */

/** A generator's complete state. Plain data, so it saves and loads for free. */
export type RngState = [number, number, number, number];

/**
 * Hash an arbitrary string into four well-mixed 32-bit values (cyrb128).
 * Used to turn a human-typable seed like "coastline-7" into generator state.
 */
function hashSeed(seed: string): RngState {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;

  for (let i = 0; i < seed.length; i++) {
    const k = seed.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }

  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);

  return [
    (h1 ^ h2 ^ h3 ^ h4) >>> 0,
    (h2 ^ h1) >>> 0,
    (h3 ^ h1) >>> 0,
    (h4 ^ h1) >>> 0,
  ];
}

/**
 * Create a generator from a seed string.
 *
 * `label` namespaces the stream, so `createRng('abc', 'mapgen')` and
 * `createRng('abc', 'combat')` produce completely independent sequences from the
 * same game seed. Always pass a label; unlabelled streams are how systems end up
 * accidentally coupled.
 */
export function createRng(seed: string, label: string): RngState {
  const state = hashSeed(`${seed}::${label}`);
  // Discard the first few draws: sfc32's output is poor until the state has mixed.
  for (let i = 0; i < 12; i++) nextU32(state);
  return state;
}

/** Advance the generator and return the next unsigned 32-bit integer. Mutates `s`. */
export function nextU32(s: RngState): number {
  const t = (((s[0] + s[1]) | 0) + s[3]) | 0;
  s[3] = (s[3] + 1) | 0;
  s[0] = s[1] ^ (s[1] >>> 9);
  s[1] = (s[2] + (s[2] << 3)) | 0;
  s[2] = (s[2] << 21) | (s[2] >>> 11);
  s[2] = (s[2] + t) | 0;
  return t >>> 0;
}

/** Next float in [0, 1). */
export function nextFloat(s: RngState): number {
  return nextU32(s) / 4294967296;
}

/** Next integer in [min, max). Throws on an empty range rather than silently returning min. */
export function nextInt(s: RngState, min: number, max: number): number {
  if (!Number.isInteger(min) || !Number.isInteger(max)) {
    throw new Error(`nextInt requires integer bounds, got [${min}, ${max})`);
  }
  if (max <= min) {
    throw new Error(`nextInt requires max > min, got [${min}, ${max})`);
  }
  return min + (nextU32(s) % (max - min));
}

/** Next float in [min, max). */
export function nextRange(s: RngState, min: number, max: number): number {
  return min + nextFloat(s) * (max - min);
}

/** True with probability `p`. */
export function chance(s: RngState, p: number): boolean {
  return nextFloat(s) < p;
}

/** Pick one element. Throws on an empty array — a silent undefined here is a bug that surfaces much later. */
export function pick<T>(s: RngState, items: readonly T[]): T {
  if (items.length === 0) throw new Error('pick() called on an empty array');
  return items[nextInt(s, 0, items.length)]!;
}

/**
 * Pick one element weighted by `weight`. Weights must be non-negative and not all zero.
 * Used constantly by the AI, so it is worth having exactly one correct implementation.
 */
export function pickWeighted<T>(s: RngState, items: readonly T[], weight: (item: T) => number): T {
  if (items.length === 0) throw new Error('pickWeighted() called on an empty array');
  let total = 0;
  for (const item of items) {
    const w = weight(item);
    if (w < 0 || !Number.isFinite(w)) throw new Error(`pickWeighted() got an invalid weight: ${w}`);
    total += w;
  }
  if (total <= 0) throw new Error('pickWeighted() requires at least one positive weight');

  let roll = nextFloat(s) * total;
  for (const item of items) {
    roll -= weight(item);
    if (roll < 0) return item;
  }
  return items[items.length - 1]!; // floating-point guard
}

/** Fisher-Yates, in place. Deterministic given the stream. */
export function shuffle<T>(s: RngState, items: T[]): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = nextInt(s, 0, i + 1);
    const a = items[i]!;
    const b = items[j]!;
    items[i] = b;
    items[j] = a;
  }
  return items;
}

/** Copy a generator's state — for speculative work that must not disturb the real stream. */
export function cloneRng(s: RngState): RngState {
  return [s[0], s[1], s[2], s[3]];
}

/**
 * Derive an independent child stream from a parent, advancing the parent once.
 * Used when a system needs a per-entity stream (e.g. one per AI civ) that stays
 * stable even as other systems draw from the parent.
 */
export function forkRng(s: RngState, label: string): RngState {
  return createRng(`${nextU32(s)}`, label);
}
